import React from 'react';
import type { TeamSecretVersion } from '../client';

interface SecretVersionsProps {
  versions: TeamSecretVersion[];
  pendingVersionId?: string | null;
  onRestore: (version: TeamSecretVersion) => void;
  onDelete: (version: TeamSecretVersion) => void;
}

const date = (value?: string | null) =>
  value ? new Date(value).toLocaleString() : '—';

const actor = (version: TeamSecretVersion) => {
  const { createdBy } = version;
  if (createdBy.kind === 'human')
    return `Human #${createdBy.userId || 'unknown'}`;
  if (createdBy.kind === 'agent')
    return `Agent #${createdBy.agentId || 'unknown'}`;
  return createdBy.kind.replace(/^./, (first) => first.toUpperCase());
};

const SecretVersions: React.FC<SecretVersionsProps> = ({
  versions,
  pendingVersionId,
  onRestore,
  onDelete,
}) => (
  <div className="secret-version-table-wrap">
    <table className="secret-version-table">
      <thead>
        <tr>
          <th>Version</th>
          <th>Actor</th>
          <th>Created</th>
          <th>Superseded</th>
          <th>Retention deadline</th>
          <th>State</th>
          <th>
            <span className="sr-only">Actions</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {versions.map((version) => {
          const expired = Boolean(
            version.retainUntil && new Date(version.retainUntil) <= new Date(),
          );
          return (
            <tr key={version.id}>
              <td>
                <code>v{version.versionNumber}</code>
              </td>
              <td>{actor(version)}</td>
              <td>{date(version.createdAt)}</td>
              <td>{date(version.supersededAt)}</td>
              <td>{date(version.retainUntil)}</td>
              <td>
                <span
                  className={`secret-state-chip ${version.current ? 'current' : ''}`}>
                  {version.current
                    ? 'Current'
                    : expired
                      ? 'Expired'
                      : 'Retained'}
                </span>
              </td>
              <td>
                {!version.current && (
                  <span className="secret-row-actions">
                    <button
                      type="button"
                      className="lightButton"
                      disabled={pendingVersionId === version.id}
                      aria-label={`Restore v${version.versionNumber}`}
                      onClick={() => onRestore(version)}>
                      Restore
                    </button>
                    <button
                      type="button"
                      className="delete-button"
                      disabled={pendingVersionId === version.id}
                      aria-label={`Delete v${version.versionNumber}`}
                      onClick={() => onDelete(version)}>
                      Delete
                    </button>
                  </span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export default SecretVersions;
